/**
 * Utility functions for working with Sharetribe categories
 */

/**
 * Fetches categories using the Sharetribe SDK (recommended approach)
 * @returns {Promise<Array>} Array of category objects
 */
export const fetchCategoriesWithSDK = async () => {
  try {
    // Import the SDK dynamically to avoid SSR issues
    const sharetribeSdk = await import('sharetribe-flex-sdk');
    
    const clientId = process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID;
    if (!clientId) {
      throw new Error('Sharetribe Client ID is not configured');
    }
    
    const sdk = sharetribeSdk.createInstance({
      clientId: clientId
    });

    console.log('Fetching categories with SDK...');
    
    // Use the SDK's assetByAlias method to fetch categories
    const response = await sdk.assetByAlias({ 
      path: 'listings/listing-categories.json', 
      alias: 'latest' 
    });

    console.log('SDK response:', response);

    if (response.data && response.data.data && response.data.data.categories) {
      return response.data.data.categories;
    } else if (response.data && response.data.categories) {
      return response.data.categories;
    } else {
      console.warn('Unexpected response format from SDK:', response);
      return [];
    }
  } catch (error) {
    console.error('Error fetching categories with SDK:', error);
    return [];
  }
};

/**
 * Fetches categories from Sharetribe's Asset Delivery API
 * @returns {Promise<Array>} Array of category objects
 */
export const fetchCategoriesFromSharetribeAssetDeliveryAPI = async () => {
  try {
    // Get the client ID from environment or config
    const clientId = process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID;
    
    if (!clientId) {
      throw new Error('Sharetribe Client ID is not configured');
    }

    // Use the proper Asset Delivery API format
    // Format: https://cdn.st-api.com/v1/assets/pub/[CLIENT_ID]/a/latest/[ASSET_PATH]
    const apiUrl = `https://cdn.st-api.com/v1/assets/pub/${clientId}/a/latest/listings/listing-categories.json`;
    
    console.log('Fetching categories from:', apiUrl);
    
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch categories: ${response.status} ${response.statusText}`);
    }
    
    const responseData = await response.json();
    
    console.log('Direct API response:', responseData);
    
    // The Asset Delivery API returns data in a specific format
    // Check if we have the expected structure
    if (responseData.data && responseData.data.categories) {
      return responseData.data.categories;
    } else if (responseData.categories) {
      // Fallback for direct data structure
      return responseData.categories;
    } else {
      console.warn('Unexpected response format from Asset Delivery API:', responseData);
      return [];
    }
  } catch (error) {
    console.error('Error fetching categories:', error);
    // Return empty array if API fails
    return [];
  }
};

/**
 * Gets only level 1 (top-level) categories with "p-" prefix
 * @param {Array} categories - Array of category objects with nested subcategories
 * @returns {Array} Array of level 1 categories with value/label format that have "p-" prefix
 */
export const getLevel1Categories = (categories) => {
  return categories
    .filter(category => category.id && category.id.startsWith('p-'))
    .map(category => ({
      value: category.id,
      label: category.name
    }));
};

 